import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useArtistAuth } from '../../hooks/useArtistAuth';
import { usePaymentFlow } from '../../hooks/usePaymentFlow';
import ApplicationForm from './ApplicationForm';
import PortfolioUpload from './PortfolioUpload';
import ReviewSubmit from './ReviewSubmit';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/SubmissionSystem.css';
import { ArrowLeft, Save, CheckCircle, AlertCircle, ArrowRight, Calendar, DollarSign } from 'lucide-react';
import WizardProgress from './WizardProgress';

const SubmissionWizard = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user, isArtist, loading: authLoading } = useArtistAuth();
  const { initiatePayment, paymentLoading, paymentError } = usePaymentFlow();
  
  const [submissionData, setSubmissionData] = useState(null);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [draft, setDraft] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [session, setSession] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [feeWaiverStatus, setFeeWaiverStatus] = useState(null); // null, 'checking', 'active', 'none'
  const [userEmail, setUserEmail] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  const TOTAL_STEPS = 3;

  useEffect(() => {
    if (user && isArtist && submissionId) {
      fetchSubmissionData();
      loadDraft();
      checkFeeWaiverOnLoad();
    }
  }, [user, isArtist, submissionId]);

  const saveDraft = useCallback(async () => {
    if (!user || !submissionId) return;
    
    try {
      setAutoSaveStatus('saving');
      
      const draftData = {
        user_id: user.id,
        submission_id: parseInt(submissionId),
        step_completed: currentStep,
        form_data: formData,
        portfolio_data: files.map(file => ({
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          artwork_title: file.artwork_title,
          artwork_medium: file.artwork_medium,
          artwork_width: file.artwork_width,
          artwork_height: file.artwork_height,
          artwork_dimensions_unit: file.artwork_dimensions_unit,
          artwork_year: file.artwork_year,
          artwork_description: file.artwork_description
        })),
        updated_at: new Date().toISOString()
      };

      if (draft?.id) {
        // Update existing draft
        const { error } = await supabase
          .from('submission_drafts')
          .update(draftData)
          .eq('id', draft.id);
        
        if (error) throw error;
      } else {
        // Create new draft
        const { data, error } = await supabase
          .from('submission_drafts')
          .insert([draftData])
          .select()
          .single();
        
        if (error) throw error;
        setDraft(data);
      }
      
      setAutoSaveStatus('saved');
      
      // Clear saved status after 3 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
      
    } catch (error) {
      console.error('Error saving draft:', error);
      setAutoSaveStatus('error');
      
      // Clear error status after 5 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    }
  }, [user, submissionId, currentStep, formData, files, draft]);

  // Auto-save functionality
  useEffect(() => {
    if (currentStep > 1 && (Object.keys(formData).length > 0 || files.length > 0)) {
      const saveInterval = setInterval(saveDraft, 30000); // Save every 30 seconds
      return () => clearInterval(saveInterval);
    }
  }, [saveDraft, currentStep]);

  const fetchSubmissionData = async () => {
    try {
      setLoading(true);
      
      // Direct SQL query to submissions_details table
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions_details')
        .select(`
          submission_id,
          title,
          theme,
          submission_deadline,
          submission_price,
          cover_image,
          collection_id,
          gallery_id,
          submission_guidelines,
          important_dates,
          artistic_guidelines,
          promotion_guidelines,
          callout_headline,
          max_allowed,
          stripe_product_id,
          stripe_payment_link,
          external_url,
          acceptance_info,
          created_at
        `)
        .eq('submission_id', parseInt(submissionId))
        .single();
      
      if (submissionError) throw submissionError;
      
      if (submissionData) {
        // Get collection details
        let collectionInfo = null;
        if (submissionData.collection_id) {
          const { data: collectionData } = await supabase
            .from('collections')
            .select('title, description, start_date, end_date')
            .eq('collection_id', submissionData.collection_id)
            .single();
          collectionInfo = collectionData;
        }
        
        // Get gallery details
        let galleryInfo = null;
        if (submissionData.gallery_id) {
          const { data: galleryData } = await supabase
            .from('galleries')
            .select('name, address, website, contact_email')
            .eq('gallery_id', submissionData.gallery_id)
            .single();
          galleryInfo = galleryData;
        }
        
        // Combine all data
        const combinedData = {
          submission_details: {
            ...submissionData,
            collection_info: collectionInfo,
            gallery_info: galleryInfo
          }
        };
        
        setSubmissionData(combinedData.submission_details);
      }
    } catch (error) {
      console.error('Error fetching submission data:', error);
      setError('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  };

  const loadDraft = async () => {
    try {
      // Direct SQL query to submission_drafts table
      const { data: draftData, error: draftError } = await supabase
        .from('submission_drafts')
        .select(`
          id,
          user_id,
          submission_id,
          step_completed,
          form_data,
          portfolio_data,
          created_at,
          updated_at,
          expires_at
        `)
        .eq('user_id', user.id)
        .eq('submission_id', parseInt(submissionId))
        .single();
      
      if (draftError && draftError.code !== 'PGRST116') {
        console.warn('Could not load draft:', draftError);
        return;
      }
      
      if (draftData) {
        // Get submission files if they exist
        let portfolioFiles = [];
        if (draftData.id) {
          const { data: filesData } = await supabase
            .from('submission_files')
            .select('*')
            .eq('draft_id', draftData.id)
            .order('sort_order, created_at');
          portfolioFiles = filesData || [];
        }
        
        setDraft(draftData);
        setFormData(draftData.form_data || {});
        setFiles(portfolioFiles);
        setCurrentStep(draftData.step_completed || 1);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  const refreshFilesFromDatabase = async () => {
    if (draft?.id) {
      try {
        console.log('🔄 Refreshing files from database for draft:', draft.id);
        const { data: filesData } = await supabase
          .from('submission_files')
          .select('*')
          .eq('draft_id', draft.id)
          .order('sort_order, created_at');
        
        console.log('📁 Files data from database:', filesData);
        
        if (filesData) {
          // Transform database files to match the expected format
          const transformedFiles = filesData.map(file => ({
            id: file.id,
            filename: file.filename,
            file_url: file.file_url,
            public_id: file.public_id,
            file_type: file.file_type,
            file_size: file.file_size,
            artwork_title: file.artwork_title || '',
            artwork_medium: file.artwork_medium || '',
            artwork_width: file.artwork_width || '',
            artwork_height: file.artwork_height || '',
            artwork_dimensions_unit: file.artwork_dimensions_unit || 'inches',
            artwork_year: file.artwork_year || '',
            artwork_description: file.artwork_description || '',
            sort_order: file.sort_order || 0,
            isNew: false
          }));
          
          console.log('🔄 Transformed files:', transformedFiles);
          setFiles(transformedFiles);
        } else {
          console.log('⚠️ No files data found in database');
        }
      } catch (error) {
        console.error('❌ Error refreshing files from database:', error);
      }
    } else {
      console.log('⚠️ No draft ID available for file refresh');
    }
  };

  const handleNext = async () => {
    if (currentStep < TOTAL_STEPS) {
      // Save progress before moving to next step
      await saveDraft();
      setCurrentStep(currentStep + 1);
      // Refresh files to ensure state consistency
      await refreshFilesFromDatabase();
    }
  };

  const handlePrevious = async () => {
    if (currentStep > 1) {
      // Save progress before moving to previous step
      await saveDraft();
      setCurrentStep(currentStep - 1);
      // Refresh files to ensure state consistency
      await refreshFilesFromDatabase();
    }
  };

  const handleStepChange = async (step) => {
    if (step >= 1 && step <= TOTAL_STEPS && step !== currentStep) {
      // Save progress before changing steps
      await saveDraft();
      setCurrentStep(step);
      // Refresh files to ensure state consistency
      await refreshFilesFromDatabase();
    }
  };

  const handleFormDataChange = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleTermsAgreement = (agreeToTerms) => {
    setFormData(prev => ({ ...prev, agreeToTerms }));
  };

  const handleFilesChange = async (newFiles) => {
    setFiles(newFiles);
    
    // Save files to submission_files table if we have a draft
    if (draft?.id && newFiles.length > 0) {
      try {
        // First, clear existing files for this draft
        await supabase
          .from('submission_files')
          .delete()
          .eq('draft_id', draft.id);
        
        // Then insert new files
        const filesToInsert = newFiles
          .filter(file => file.file_url || file.preview || file.url) // Only save files with valid URLs
          .map((file, index) => ({
            draft_id: draft.id,
            user_id: user.id,
            filename: file.name || file.filename || `artwork_${index + 1}`,
            file_url: file.file_url || file.preview || file.url || '',
            public_id: file.public_id || null, // Store Cloudinary public ID
            file_size: file.size || file.file_size || 0,
            file_type: file.type || file.file_type || 'image/jpeg',
            sort_order: index,
            artwork_title: file.artwork_title || '',
            artwork_medium: file.artwork_medium || '',
            artwork_width: file.artwork_width || null,
            artwork_height: file.artwork_height || null,
            artwork_dimensions_unit: file.artwork_dimensions_unit || 'inches',
            artwork_year: file.artwork_year || null,
            artwork_description: file.artwork_description || ''
          }));
        
        const { error: filesError } = await supabase
          .from('submission_files')
          .insert(filesToInsert);
        
        if (filesError) {
          console.error('Error saving portfolio files:', filesError);
        } else {
          console.log('Portfolio files saved successfully');
        }
      } catch (error) {
        console.error('Error saving portfolio files:', error);
      }
    }
  };

  const checkFeeWaiver = async (submissionId, userId) => {
    try {
      console.log('🔍 Checking fee waiver for user:', userId, 'submission:', submissionId);
      
      // Check if user has a fee waiver for this submission
      const { data: waiver, error } = await supabase
        .from('artist_submission_waivers')
        .select('*')
        .eq('user_id', userId)
        .eq('submission_id', submissionId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Fee waiver check error:', error);
        throw error; // PGRST116 = no rows returned
      }
      
      if (waiver) {
        console.log('✅ Fee waiver found:', waiver);
        // Check if waiver is still valid (not expired)
        const isExpired = waiver.expires_at ? new Date(waiver.expires_at) < new Date() : false;
        if (isExpired) {
          console.log('❌ Fee waiver is expired');
          return null;
        }
        console.log('✅ Fee waiver is valid and active');
      } else {
        console.log('❌ No fee waiver found');
      }
      
      return waiver || null;
    } catch (err) {
      console.error('Error checking fee waiver:', err);
      return null;
    }
  };

  const checkFeeWaiverOnLoad = async () => {
    if (!user || !submissionId) return;
    
    try {
      setFeeWaiverStatus('checking');
      console.log('🔄 Checking fee waiver status on component load...');
      
      const waiver = await checkFeeWaiver(parseInt(submissionId), user.id);
      
      if (waiver) {
        console.log('✅ Fee waiver active - payment section will be hidden');
        setFeeWaiverStatus('active');
      } else {
        console.log('❌ No active fee waiver - payment section will be shown');
        setFeeWaiverStatus('none');
      }
    } catch (err) {
      console.error('Error checking fee waiver on load:', err);
      setFeeWaiverStatus('none'); // Default to showing payment if check fails
    }
  };

  const submitApplication = async () => {
    try {
      // Create or update submission application
      const applicationData = {
        user_id: user.id,
        submission_id: parseInt(submissionId),
        status: 'submitted',
        submitted_at: new Date().toISOString()
      };

      // Check if application already exists
      const { data: existingApp, error: checkError } = await supabase
        .from('submission_applications')
        .select('id')
        .eq('user_id', user.id)
        .eq('submission_id', parseInt(submissionId))
        .maybeSingle();

      if (checkError) throw checkError;

      let applicationError = null;
      
      if (existingApp) {
        // Update existing application
        const { error: updateError } = await supabase
          .from('submission_applications')
          .update({
            status: 'submitted',
            submitted_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existingApp.id);
        applicationError = updateError;
      } else {
        // Insert new application
        const { error: insertError } = await supabase
          .from('submission_applications')
          .insert(applicationData);
        applicationError = insertError;
      }

      if (applicationError) throw applicationError;

      // Save portfolio files
      for (const file of files) {
        const fileData = {
          user_id: user.id,
          submission_id: parseInt(submissionId),
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          artwork_title: file.artwork_title,
          artwork_medium: file.artwork_medium,
          artwork_width: file.artwork_width,
          artwork_height: file.artwork_height,
          artwork_dimensions_unit: file.artwork_dimensions_unit,
          artwork_year: file.artwork_year,
          artwork_description: file.artwork_description,
          uploaded_at: new Date().toISOString()
        };

        const { error: fileError } = await supabase
          .from('submission_files')
          .insert([fileData]);

        if (fileError) {
          console.error('Error saving file:', fileError);
          // Continue with other files even if one fails
        }
      }

      // Clear draft after successful submission
      if (draft?.id) {
        await supabase
          .from('submission_drafts')
          .delete()
          .eq('id', draft.id);
      }

      console.log('✅ Application submitted successfully!');
      
        // Navigate to success page
        navigate(`/artist/submissions/${submissionId}/success`);    } catch (error) {
      console.error('Error submitting application:', error);
      throw new Error('Failed to submit application: ' + error.message);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate required data
      if (files.length === 0) {
        throw new Error('Please upload at least one artwork before submitting.');
      }
      
      // Check if user has agreed to terms
      if (!formData.agreeToTerms) {
        throw new Error('Please agree to the terms and conditions before submitting.');
      }
      
      // Check for fee waiver first
      console.log('📝 About to check fee waiver with:', { 
        submissionId: parseInt(submissionId), 
        userId: user.id,
        submissionIdRaw: submissionId,
        userObject: user
      });
      const feeWaiver = await checkFeeWaiver(parseInt(submissionId), user.id);
      
      if (feeWaiver) {
        // Fee waived, submit directly without payment
        console.log('✅ Fee waiver found, submitting application directly...');
        await submitApplication();
        return;
      } else {
        console.log('❌ No fee waiver found, proceeding with payment flow...');
      }
      
      // No fee waiver, check if payment is required
      if (!submissionData?.stripe_product_id) {
        throw new Error('This submission does not have payment configured. Please contact support.');
      }
      
      // Create or update submission application first
      const applicationData = {
        user_id: user.id,
        submission_id: parseInt(submissionId),
        status: 'draft', // Start as draft, will be updated after payment
        submitted_at: new Date().toISOString()
      };

      // First check if application already exists
      const { data: existingApp, error: checkError } = await supabase
        .from('submission_applications')
        .select('id')
        .eq('user_id', user.id)
        .eq('submission_id', parseInt(submissionId))
        .maybeSingle(); // Use maybeSingle to avoid errors when no rows found

      if (checkError) {
        throw checkError;
      }

      let applicationError = null;
      
      if (existingApp) {
        // Update existing application
        const { error: updateError } = await supabase
          .from('submission_applications')
          .update({
            status: 'draft',
            submitted_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existingApp.id);
        applicationError = updateError;
      } else {
        // Insert new application
        const { error: insertError } = await supabase
          .from('submission_applications')
          .insert(applicationData);
        applicationError = insertError;
      }

      if (applicationError) throw applicationError;

      console.log('✅ Application saved successfully, initiating payment...');
      console.log('Payment data:', { userId: user.id, stripeProductId: submissionData.stripe_product_id, submissionId: parseInt(submissionId) });

      // Initiate payment using the payment flow hook
      const paymentResult = await initiatePayment({
        userId: user.id,
        stripeProductId: submissionData.stripe_product_id,
        submissionId: parseInt(submissionId)
      });

      console.log('Payment result:', paymentResult);

      if (paymentResult.success) {
        // Payment initiated successfully - user will be redirected to Stripe
        // The application status will be updated when payment is completed
        console.log('Payment initiated successfully');
        
        // Clear draft after successful submission
        if (draft?.id) {
          await supabase
            .from('submission_drafts')
            .delete()
            .eq('id', draft.id);
        }
        
        // Navigate to success page
        navigate(`/artist/submissions/${submissionId}/success`);
      } else {
        throw new Error('Failed to initiate payment. Please try again.');
      }
      
    } catch (error) {
      console.error('Error submitting application:', error);
      setError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const then = new Date(deadline);
    const diff = then.getTime() - now.getTime();

    if (diff <= 0) {
      return 'Expired';
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    // Only show minutes if less than 1 hour from deadline
    if (days === 0 && hours === 0) {
      return `${minutes}m`;
    } else if (days === 0) {
      return `${hours}h`;
    } else {
      return `${days}d ${hours}h`;
    }
  };

  if (loading) {
    return (
      <div className="submission-loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (!submissionData) {
    return (
      <div className="submission-loading-container">
        <div className="submission-empty-state">
          <h2 className="submission-empty-title">Submission Not Found</h2>
          <p className="submission-empty-description">
            The submission you're looking for doesn't exist or you don't have access to it.
          </p>
          <button
            onClick={() => navigate('/artist/submissions')}
            className="submission-btn submission-btn-primary"
          >
            Back to Submissions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="submission-wizard-content">
      {/* Header */}
      <div className="submission-header">
        <div className="submission-header-content">
          <div className="submission-header-row">
            <div className="submission-header-left">
              {/* Cover Image */}
              {submissionData.cover_image && (
                <div className="submission-cover-thumbnail">
                  <img 
                    src={submissionData.cover_image} 
                    alt={submissionData.title}
                  />
                </div>
              )}
              
              {/* Title and Actions */}
              <div className="submission-header-details">
                <h1 className="submission-title">
                  {submissionData.title}
                </h1>
                <div className="submission-header-actions">
                  <button
                    onClick={() => navigate(`/submissions/${submissionId}`)}
                    className="submission-btn submission-btn-secondary submission-btn-small"
                  >
                    <CheckCircle className="submission-btn-icon" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
            
            {/* Save Button - Right Side */}
            <div className="submission-header-right">
              {/* Auto-save status */}
              {autoSaveStatus === 'saving' && (
                <div className="submission-save-status saving">
                  <div className="submission-spinner"></div>
                  Saving...
                </div>
              )}
              {autoSaveStatus === 'saved' && (
                <div className="submission-save-status saved">
                  <CheckCircle className="submission-btn-icon" />
                  Saved
                </div>
              )}
              {autoSaveStatus === 'error' && (
                <div className="submission-save-status error">
                  <AlertCircle className="submission-btn-icon" />
                  Save failed
                </div>
              )}
              
              <button
                onClick={saveDraft}
                disabled={saving}
                className="submission-btn submission-btn-primary"
              >
                <Save className="submission-btn-icon" />
                Save Draft
              </button>
            </div>
          </div>
          
          {/* Deadline and Fee - Full Width Rows */}
          <div className="submission-header-info">
            <div className="submission-info-item">
              <Calendar className="submission-info-icon" />
              <span className="submission-info-label">Deadline:</span>
              <span className="submission-info-date">
                {submissionData.submission_deadline ? 
                  new Date(submissionData.submission_deadline).toLocaleDateString() : 
                  'No deadline'
                }
              </span>
              {submissionData.submission_deadline && (
                <span className="submission-countdown">
                  {getTimeRemaining(submissionData.submission_deadline)}
                </span>
              )}
            </div>
            {submissionData.submission_price && (
              <div className="submission-info-item">
                <DollarSign className="submission-info-icon" />
                <span className="submission-info-label">Fee:</span>
                <span className="submission-info-value">${submissionData.submission_price}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="submission-main-content">
        {/* Progress Bar */}
        <WizardProgress 
          currentStep={currentStep} 
          totalSteps={TOTAL_STEPS}
          onStepClick={handleStepChange}
        />

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Error</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="submission-content-card">
          {currentStep === 1 && (
            <ApplicationForm
              submissionData={submissionData}
              formData={formData}
              onChange={handleFormDataChange}
              onNext={handleNext}
            />
          )}
          
          {currentStep === 2 && (
            <PortfolioUpload
              files={files}
              onChange={handleFilesChange}
              onNext={handleNext}
              onPrevious={handlePrevious}
              maxImages={submissionData?.max_allowed || 5}
            />
          )}
          
          {currentStep === 3 && (
            <ReviewSubmit
              submissionData={submissionData}
              formData={formData}
              files={files}
              onSubmit={handleSubmit}
              onPrevious={handlePrevious}
              saving={saving}
              error={error}
              onTermsAgreement={handleTermsAgreement}
              feeWaiverStatus={feeWaiverStatus}
            />
          )}
        </div>

        {/* Step Navigation */}
        {currentStep < TOTAL_STEPS && (
          <div className="mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="submission-btn submission-btn-secondary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </button>
            )}
            
            <button
              onClick={handleNext}
              className="submission-btn submission-btn-primary"
            >
              Next Step
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionWizard;
